/**
 * ONE Mobile — scan-first, role-personalised operations home.
 *
 * Optimised for phone/tablet. Primary action is the persistent SCAN button
 * (camera + manual). Scanning resolves to context and suggests next actions.
 */
import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScanLine, Package, Truck, Wrench, User2, Search, ArrowRight, X, RefreshCw, Warehouse } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function OneMobile() {
  const [home, setHome] = useState(null);
  const [showScan, setShowScan] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get("/mobile/home").then(({ data }) => setHome(data));
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-md mx-auto space-y-5" data-testid="one-mobile">
      <header>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">ONE Mobile</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight mt-1">
          {greeting()}, {home?.role?.replace(/_/g, " ") || "there"}.
        </h1>
        <p className="text-sm text-slate-600 mt-1">Everything looks healthy. Scan an asset to start.</p>
      </header>

      <button
        onClick={() => setShowScan(true)}
        data-testid="mobile-scan-btn"
        className="w-full h-16 rounded-xl bg-slate-900 text-white text-base font-semibold tracking-tight flex items-center justify-center gap-2 shadow-lg active:scale-[0.98] transition-transform"
      >
        <ScanLine className="h-6 w-6" strokeWidth={1.6} /> Scan
      </button>

      <section className="grid grid-cols-2 gap-2" data-testid="mobile-queues">
        {(home?.queues || []).map((q) => (
          <div key={q.key} className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{q.label}</div>
            <div className="mt-1 text-2xl font-heading font-semibold text-slate-900 tabular-nums">{q.count}</div>
          </div>
        ))}
        {!home && <div className="text-sm text-slate-500 col-span-2">Loading…</div>}
      </section>

      <QuickActions />

      {showScan && (
        <ScanDialog
          onClose={() => { setShowScan(false); setResult(null); }}
          onResult={setResult}
          result={result}
        />
      )}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function QuickActions() {
  const navigate = useNavigate();
  const actions = [
    { key: "warehouse", label: "Warehouse scan",   icon: Warehouse, hint: "Inward · rack · pick · pack · dispatch",
      onClick: () => navigate("/mobile/warehouse") },
    { key: "receive", label: "Warehouse inward",  icon: Package,  hint: "Scan → verify → put away",
      onClick: () => navigate("/mobile/warehouse") },
    { key: "dispatch", label: "Dispatch shipment", icon: Truck,    hint: "Scan → verify → capture AWB",
      onClick: () => navigate("/mobile/warehouse") },
    { key: "service", label: "Service case",       icon: Wrench,   hint: "Scan → diagnose → update" },
    { key: "employee",label: "Employee lookup",    icon: User2,    hint: "Find contact + address" },
  ];
  return (
    <section className="grid grid-cols-2 gap-2" data-testid="mobile-actions">
      {actions.map((a) => (
        <button key={a.key}
             type="button"
             onClick={a.onClick}
             data-testid={`mobile-action-${a.key}`}
             className="text-left bg-white border border-slate-200 rounded-lg p-3 active:scale-[0.98] transition-transform">
          <a.icon className="h-4 w-4 text-slate-600" strokeWidth={1.6} />
          <div className="mt-1 text-sm font-semibold text-slate-900">{a.label}</div>
          <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{a.hint}</div>
        </button>
      ))}
    </section>
  );
}

function ScanDialog({ onClose, onResult, result }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/mobile/scan", { code: code.trim() });
      onResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="mobile-scan-dialog">
      <header className="flex items-center gap-3 px-4 h-14 border-b border-slate-100">
        <button onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Close" data-testid="mobile-scan-close">
          <X className="h-5 w-5" />
        </button>
        <div className="text-sm font-semibold text-slate-900">Scan</div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="bg-slate-900 rounded-xl h-40 grid place-items-center text-slate-400 text-xs relative">
          <div className="absolute inset-4 border border-amber-400/50 rounded-lg" />
          <div className="text-center">
            <ScanLine className="h-8 w-8 text-amber-400 mx-auto animate-pulse" strokeWidth={1.4} />
            <div className="mt-2">Camera-scan opens on native mobile. Use manual entry below.</div>
          </div>
        </div>

        <form onSubmit={submit}>
          <label className="block">
            <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Manual entry</div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input ref={inputRef} value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="Serial · IMEI · AWB · order number"
                  className="w-full h-12 pl-9 pr-3 rounded-md border border-slate-300 text-sm bg-white"
                  data-testid="mobile-scan-input" />
              </div>
              <button type="submit" disabled={loading}
                className="h-12 px-4 rounded-md bg-slate-900 text-white text-sm disabled:opacity-60"
                data-testid="mobile-scan-submit">
                {loading ? "…" : "Find"}
              </button>
            </div>
          </label>
        </form>

        {result && <ScanResult result={result} onReset={() => { setCode(""); onResult(null); }} />}
      </div>
    </div>
  );
}

function ScanResult({ result, onReset }) {
  if (result.kind === "unknown") {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-700" data-testid="scan-unknown">
        <div className="font-semibold text-slate-900">No match</div>
        <div className="text-xs text-slate-500 mt-1">{result.hint}</div>
        <button onClick={onReset} className="mt-3 text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Scan again</button>
      </div>
    );
  }
  const entity = result.entity || {};
  const title = entity.serial_number || entity.shipment_number || entity.pickup_number || entity.order_number || entity.name || "(match)";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="scan-result">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border border-emerald-200 text-emerald-700 bg-emerald-50">{result.kind}</span>
        <div className="text-sm font-semibold text-slate-900 font-mono">{title}</div>
      </div>
      <dl className="text-xs text-slate-600 grid grid-cols-2 gap-y-1">
        {Object.entries(entity).slice(0, 8).map(([k, v]) => v && typeof v !== "object" ? (
          <div key={k} className="col-span-2 flex gap-2 border-b border-slate-50 py-1">
            <dt className="w-32 shrink-0 text-slate-400 uppercase tracking-widest text-[10px]">{k.replaceAll("_", " ")}</dt>
            <dd className="text-slate-800">{String(v)}</dd>
          </div>
        ) : null)}
      </dl>
      <div className="grid grid-cols-2 gap-2 pt-1">
        {(result.actions || []).map((a) => (
          <button key={a.key} className="h-11 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-800 text-sm flex items-center justify-between px-3" data-testid={`scan-action-${a.key}`}>
            {a.label}
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </button>
        ))}
      </div>
      <button onClick={onReset} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <RefreshCw className="h-3 w-3" /> Scan another
      </button>
    </div>
  );
}
